import React from 'react'

import { Chip, InputAdornment, TextField, useTheme } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'

import { Container } from '../../../components'
import {
    AssetCondition,
    filterInventoryAssets,
    flattenUnitInventory,
    formatConditionLabel,
    formatStatusLabel,
    getConditionColor,
    getStatusColor,
    InventoryAssetStatus,
    InventoryFilters,
    normalizeInventoryAreas,
    UnitInventoryResponse,
    AreaLabelContext,
} from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    inventory: UnitInventoryResponse
    filters: InventoryFilters
    areaLabelContext?: AreaLabelContext
    onFiltersChange: (filters: InventoryFilters) => void
}

export const InventoryFilterBar: React.FC<Props> = ({
    inventory,
    filters,
    areaLabelContext,
    onFiltersChange,
}) => {
    const theme = useTheme()

    const allAssets = flattenUnitInventory(inventory)
    const filteredAssets = filterInventoryAssets(
        allAssets,
        filters,
        areaLabelContext,
    )

    const areaLabels = normalizeInventoryAreas(allAssets, areaLabelContext).map(
        (group) => group.displayLabel,
    )

    const conditions: AssetCondition[] = ['new', 'good', 'fair', 'poor']
    const statuses = Array.from(
        new Set(allAssets.map((asset) => asset.status)),
    ) as InventoryAssetStatus[]

    const hasActiveFilters =
        Boolean(filters.search) ||
        Boolean(filters.area) ||
        Boolean(filters.condition) ||
        Boolean(filters.status)

    const areaChipStyle = (active: boolean): React.CSSProperties => ({
        marginRight: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        fontWeight: active ? 600 : 500,
        fontSize: 12,
        height: 28,
        backgroundColor: active ? theme.palette.primary.main : 'transparent',
        color: active
            ? theme.palette.primary.contrastText
            : theme.palette.text.secondary,
        border: active
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.grey[300]}`,
    })

    const conditionChipStyle = (
        condition: AssetCondition,
        active: boolean,
    ): React.CSSProperties => {
        const color = getConditionColor(condition)
        return {
            marginRight: theme.spacing(0.5),
            marginBottom: theme.spacing(0.5),
            fontWeight: 600,
            fontSize: 12,
            height: 28,
            backgroundColor: active ? `${color}18` : 'transparent',
            color: active ? color : theme.palette.text.secondary,
            border: `1px solid ${
                active ? `${color}50` : theme.palette.grey[300]
            }`,
        }
    }

    const statusChipStyle = (
        status: InventoryAssetStatus,
        active: boolean,
    ): React.CSSProperties => {
        const color = getStatusColor(status)
        return {
            marginRight: theme.spacing(0.5),
            marginBottom: theme.spacing(0.5),
            fontWeight: 600,
            fontSize: 12,
            height: 28,
            backgroundColor: active ? `${color}18` : 'transparent',
            color: active ? color : theme.palette.text.secondary,
            border: `1px solid ${
                active ? `${color}50` : theme.palette.grey[300]
            }`,
        }
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: theme.spacing(1.5),
                padding: theme.spacing(2, 2.5),
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
            }}
        >
            <Container style={{ gap: theme.spacing(2), alignItems: 'center' }}>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search assets..."
                    value={filters.search}
                    onChange={(event) =>
                        onFiltersChange({
                            ...filters,
                            search: event.target.value,
                        })
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    fontSize="small"
                                    style={{
                                        color: theme.palette.text.disabled,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    style={{ minWidth: 240, flex: 1, maxWidth: 360 }}
                />
                <span
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        color: theme.palette.text.secondary,
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                    }}
                >
                    {hasActiveFilters
                        ? `${filteredAssets.length} of ${allAssets.length} assets`
                        : `${allAssets.length} assets`}
                </span>
            </Container>

            <Container
                style={{
                    flexWrap: 'wrap',
                    gap: theme.spacing(0.5),
                    alignItems: 'center',
                }}
            >
                {areaLabels.length > 1 && (
                    <>
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: theme.palette.text.disabled,
                                marginRight: theme.spacing(0.5),
                            }}
                        >
                            Area
                        </span>
                        <Chip
                            size="small"
                            label="All"
                            style={areaChipStyle(!filters.area)}
                            onClick={() =>
                                onFiltersChange({ ...filters, area: null })
                            }
                        />
                        {areaLabels.map((label) => (
                            <Chip
                                key={label}
                                size="small"
                                label={label}
                                style={areaChipStyle(filters.area === label)}
                                onClick={() =>
                                    onFiltersChange({
                                        ...filters,
                                        area:
                                            filters.area === label
                                                ? null
                                                : label,
                                    })
                                }
                            />
                        ))}
                        <span
                            style={{
                                width: 1,
                                height: 20,
                                backgroundColor: theme.palette.divider,
                                marginLeft: theme.spacing(1),
                                marginRight: theme.spacing(1),
                            }}
                        />
                    </>
                )}

                <span
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: theme.palette.text.disabled,
                        marginRight: theme.spacing(0.5),
                    }}
                >
                    Condition
                </span>
                {conditions.map((condition) => (
                    <Chip
                        key={condition}
                        size="small"
                        label={formatConditionLabel(condition)}
                        style={conditionChipStyle(
                            condition,
                            filters.condition === condition,
                        )}
                        onClick={() =>
                            onFiltersChange({
                                ...filters,
                                condition:
                                    filters.condition === condition
                                        ? null
                                        : condition,
                            })
                        }
                    />
                ))}

                {statuses.length > 1 && (
                    <>
                        <span
                            style={{
                                width: 1,
                                height: 20,
                                backgroundColor: theme.palette.divider,
                                marginLeft: theme.spacing(1),
                                marginRight: theme.spacing(1),
                            }}
                        />
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: theme.palette.text.disabled,
                                marginRight: theme.spacing(0.5),
                            }}
                        >
                            Status
                        </span>
                        {statuses.map((status) => (
                            <Chip
                                key={status}
                                size="small"
                                label={formatStatusLabel(status)}
                                style={statusChipStyle(
                                    status,
                                    filters.status === status,
                                )}
                                onClick={() =>
                                    onFiltersChange({
                                        ...filters,
                                        status:
                                            filters.status === status
                                                ? null
                                                : status,
                                    })
                                }
                            />
                        ))}
                    </>
                )}
            </Container>
        </Container>
    )
}

export const getFilteredGroupedInventory = (
    inventory: UnitInventoryResponse,
    filters: InventoryFilters,
    areaLabelContext?: AreaLabelContext,
) => {
    const allAssets = flattenUnitInventory(inventory)
    const filteredAssets = filterInventoryAssets(
        allAssets,
        filters,
        areaLabelContext,
    )
    return normalizeInventoryAreas(filteredAssets, areaLabelContext)
}
